/**
 * @fileoverview Custom export engine (MOR-1048).
 *
 * Pure functions that run a `CustomExportSpec` (convex/schema.ts) over a
 * tabular dataset — the already-rendered rows behind any export surface —
 * and produce the grid that previews on screen and exports to Excel.
 *
 * Datasets address columns by header name: the surfaces feed the tool the
 * same `ExportSheet` rows they hand to the Excel exporter, so the header
 * is the stable key a saved spec can re-run against on any job.
 *
 * Used by:
 * - src/components/customExport/CustomExportBuilder.tsx
 * - src/ui/composed/ExportButton.tsx (via CustomExportDialog)
 */

import type {
  CustomExportMeasure,
  CustomExportSpec,
} from "@cvx/savedCustomExports/schema";
import type { ExportContent, ExportSheet } from "@/utils/export";

export type CustomExportCell = string | number | Date | null | undefined;

export interface CustomExportDataset {
  /** Stable key the saved specs are filed under, e.g. "sheet:assets". */
  key: string;
  label: string;
  headers: string[];
  rows: CustomExportCell[][];
}

/** A blank spec that already reads like something useful: the full table. */
export const DEFAULT_CUSTOM_EXPORT_SPEC: CustomExportSpec = {
  mode: "table",
  columns: [],
  filters: [],
  rows: [],
  measures: [],
  showTotals: true,
};

export const AGG_LABELS: Record<CustomExportMeasure["agg"], string> = {
  sum: "Sum",
  count: "Count",
  avg: "Average",
  min: "Min",
  max: "Max",
};

export function measureLabel(m: CustomExportMeasure): string {
  return `${AGG_LABELS[m.agg]} of ${m.column}`;
}

/**
 * Default aggregation for a column: sum when most populated cells are
 * numeric (including rendered money), otherwise count.
 */
export function guessAgg(
  dataset: CustomExportDataset,
  column: string,
): CustomExportMeasure["agg"] {
  const idx = dataset.headers.indexOf(column);
  if (idx === -1) return "count";
  let numeric = 0;
  let nonempty = 0;
  for (const row of dataset.rows) {
    if (cellText(row[idx]) === "") continue;
    nonempty += 1;
    if (cellNumber(row[idx]) !== null) numeric += 1;
  }
  return nonempty > 0 && numeric * 2 >= nonempty ? "sum" : "count";
}

function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "sheet"
  );
}

/**
 * Datasets from the surface's existing `ExportContent`. Section-label rows
 * are display furniture, not data, so they are dropped; keys derive from
 * the sheet name (stable per surface) rather than the job-specific title,
 * so a spec saved on one job re-runs on every job's register.
 */
export function datasetsFromContent(
  content: ExportContent,
): CustomExportDataset[] {
  const sheets = (content.sheets ?? []).filter(
    (s): s is ExportSheet => !s.omitFromExcel && s.headers.length > 0,
  );
  const datasets: CustomExportDataset[] = [];
  const usedKeys = new Set<string>();
  for (const sheet of sheets) {
    const sections = new Set(sheet.sectionRows ?? []);
    let key = `sheet:${slugify(sheet.name)}`;
    for (let i = 2; usedKeys.has(key); i++) {
      key = `sheet:${slugify(sheet.name)}-${i}`;
    }
    usedKeys.add(key);
    datasets.push({
      key,
      label: sheet.name,
      headers: [...sheet.headers],
      rows: sheet.rows.filter((_, i) => !sections.has(i)),
    });
  }
  return datasets;
}

/** Display/compare text for a cell. Dates keep ISO order so sorting works. */
export function cellText(cell: CustomExportCell): string {
  if (cell === null || cell === undefined) return "";
  if (cell instanceof Date) return cell.toISOString().slice(0, 10);
  return String(cell);
}

/** Numeric value of a cell, tolerating rendered strings ("$1,234.50"). */
export function cellNumber(cell: CustomExportCell): number | null {
  if (typeof cell === "number") return Number.isFinite(cell) ? cell : null;
  if (cell instanceof Date) return cell.getTime();
  if (typeof cell === "string") {
    const cleaned = cell.replace(/[$,%\s]/g, "");
    if (cleaned === "" || cleaned === "-") return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Numeric-aware comparison so "10" sorts after "9" and money sorts as money. */
function compareCells(a: CustomExportCell, b: CustomExportCell): number {
  const an = cellNumber(a);
  const bn = cellNumber(b);
  if (an !== null && bn !== null) return an - bn;
  return cellText(a).localeCompare(cellText(b));
}

export function distinctValues(
  dataset: CustomExportDataset,
  column: string,
): string[] {
  const idx = dataset.headers.indexOf(column);
  if (idx === -1) return [];
  const seen = new Set<string>();
  for (const row of dataset.rows) {
    seen.add(cellText(row[idx]));
  }
  return [...seen].sort((a, b) => compareCells(a, b));
}

function applyFilters(
  dataset: CustomExportDataset,
  spec: CustomExportSpec,
): CustomExportCell[][] {
  const active = spec.filters
    .map((f) => ({ ...f, idx: dataset.headers.indexOf(f.column) }))
    .filter((f) => f.idx !== -1 && f.values.length > 0);
  if (active.length === 0) return dataset.rows;
  return dataset.rows.filter((row) =>
    active.every((f) => {
      const match = f.values.includes(cellText(row[f.idx]));
      return f.negate ? !match : match;
    }),
  );
}

function aggregate(
  values: CustomExportCell[],
  agg: CustomExportMeasure["agg"],
): number | "" {
  if (agg === "count") {
    let n = 0;
    for (const value of values) if (cellText(value) !== "") n++;
    return n;
  }
  const nums = values.map(cellNumber).filter((n): n is number => n !== null);
  if (nums.length === 0) return "";
  switch (agg) {
    case "sum":
      return nums.reduce((a, b) => a + b, 0);
    case "avg":
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case "min":
      return Math.min(...nums);
    case "max":
      return Math.max(...nums);
    default: {
      const _exhaustive: never = agg;
      return _exhaustive;
    }
  }
}

export interface CustomExportResult {
  headers: string[];
  rows: CustomExportCell[][];
  /** Source rows that survived the filters. */
  sourceCount: number;
}

/** Pivot mode needs a shape before it can run; table mode always can. */
export function specIsRunnable(
  dataset: CustomExportDataset,
  spec: CustomExportSpec,
): boolean {
  if (spec.mode === "table") return true;
  return (
    spec.rows.some((r) => dataset.headers.includes(r)) &&
    spec.measures.length > 0
  );
}

export function runCustomExport(
  dataset: CustomExportDataset,
  spec: CustomExportSpec,
): CustomExportResult {
  const filtered = applyFilters(dataset, spec);

  if (spec.mode === "table") {
    const selected = spec.columns.filter((c) => dataset.headers.includes(c));
    const columns = selected.length > 0 ? selected : dataset.headers;
    const indexes = columns.map((c) => dataset.headers.indexOf(c));
    let rows = filtered.map((row) => indexes.map((i) => row[i]));
    if (spec.sortBy && columns.includes(spec.sortBy)) {
      const sortIdx = columns.indexOf(spec.sortBy);
      const mul = spec.sortDir === "desc" ? -1 : 1;
      rows = [...rows].sort(
        (a, b) => compareCells(a[sortIdx], b[sortIdx]) * mul,
      );
    }
    return { headers: [...columns], rows, sourceCount: filtered.length };
  }

  // Pivot mode.
  const rowDims = spec.rows
    .filter((r) => dataset.headers.includes(r))
    .slice(0, 2);
  const rowIdx = rowDims.map((r) => dataset.headers.indexOf(r));
  const measures = spec.measures.filter((m) =>
    dataset.headers.includes(m.column),
  );
  const pivotIdx =
    spec.pivotColumn && dataset.headers.includes(spec.pivotColumn)
      ? dataset.headers.indexOf(spec.pivotColumn)
      : -1;

  if (rowDims.length === 0 || measures.length === 0) {
    return { headers: [], rows: [], sourceCount: filtered.length };
  }

  const pivotValues =
    pivotIdx === -1
      ? []
      : [...new Set(filtered.map((row) => cellText(row[pivotIdx])))].sort(
          (a, b) => compareCells(a, b),
        );

  // Group rows by the row-dimension key.
  const groups = new Map<
    string,
    { labels: CustomExportCell[]; rows: CustomExportCell[][] }
  >();
  for (const row of filtered) {
    const labels = rowIdx.map((i) => row[i]);
    const key = labels.map(cellText).join(" ");
    let group = groups.get(key);
    if (!group) {
      group = { labels, rows: [] };
      groups.set(key, group);
    }
    group.rows.push(row);
  }

  const measureCell = (
    rows: CustomExportCell[][],
    m: CustomExportMeasure,
    pivotValue: string | null,
  ): number | "" => {
    const mIdx = dataset.headers.indexOf(m.column);
    const scoped =
      pivotValue === null
        ? rows
        : rows.filter((row) => cellText(row[pivotIdx]) === pivotValue);
    return aggregate(
      scoped.map((row) => row[mIdx]),
      m.agg,
    );
  };

  const cellsFor = (rows: CustomExportCell[][]): (number | "")[] => {
    const cells: (number | "")[] = [];
    if (pivotValues.length > 0) {
      for (const pv of pivotValues) {
        for (const m of measures) cells.push(measureCell(rows, m, pv));
      }
      // Total column group, like the pivot builder's trailing Total.
      for (const m of measures) cells.push(measureCell(rows, m, null));
    } else {
      for (const m of measures) cells.push(measureCell(rows, m, null));
    }
    return cells;
  };

  const headers = [...rowDims];
  if (pivotValues.length > 0) {
    for (const pv of pivotValues) {
      for (const m of measures) {
        headers.push(
          measures.length > 1 || pivotValues.length > 0
            ? `${pv || "(blank)"} — ${measureLabel(m)}`
            : pv || "(blank)",
        );
      }
    }
    for (const m of measures) headers.push(`Total — ${measureLabel(m)}`);
  } else {
    for (const m of measures) headers.push(measureLabel(m));
  }

  const bodyRows = [...groups.values()]
    .sort((a, b) => {
      for (let i = 0; i < a.labels.length; i++) {
        const cmp = compareCells(a.labels[i], b.labels[i]);
        if (cmp !== 0) return cmp;
      }
      return 0;
    })
    .map((group) => [...group.labels, ...cellsFor(group.rows)]);

  if (spec.showTotals && bodyRows.length > 0) {
    const totalLabels: CustomExportCell[] = rowDims.map((_, i) =>
      i === 0 ? "Total" : "",
    );
    bodyRows.push([...totalLabels, ...cellsFor(filtered)]);
  }

  return { headers, rows: bodyRows, sourceCount: filtered.length };
}
